// The bonus dashboard (PRD §3.4, BP1–BP5; rev. 1.6). Every person × bonus type for one day: the
// letter's target, any override, and the amount that counts, with OTE per the letter and with
// overrides, and totals by type. Each amount opens the edits that apply to it; they are dated
// records on the person's card, previewed and audited like any pay change.

import { useState, type ReactNode } from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Switch,
  Tooltip,
} from "@fluentui/react-components";
import { overridesOn, PAY_TYPE_LABEL, today, type BonusType, type PayType } from "../../../core";
import { errorText, send, useApi, type BonusDashboardResponse, type BonusRow } from "../api";
import { DateInput } from "../DateInput";
import { count, day, money } from "../format";
import { href } from "../router";
import { ErrorBar, Fact, Loading, Page, useMe, Warnings, ArchivedSwitch, useShowArchived } from "../ui";
import { BonusLetterDialog, BonusOverrideDialog, EndRecordDialog, VoidDialog } from "./PersonDialogs";

type Open =
  | { kind: "letter"; row: BonusRow }
  | { kind: "override"; row: BonusRow; typeId: string | null }
  | { kind: "end"; row: BonusRow; recordId: string; label: string }
  | { kind: "void"; row: BonusRow; recordId: string; label: string }
  | { kind: "types" }
  | null;

export function BonusDashboard() {
  const me = useMe();
  const editable = me.can("people.edit");
  const [asOf, setAsOf] = useState(today());
  const [showArchived, setShowArchived] = useShowArchived("bonus");
  const data = useApi<BonusDashboardResponse>(`/bonus?asOf=${asOf}${showArchived ? "&archived=1" : ""}`);
  const [open, setOpen] = useState<Open>(null);
  const close = (changed: boolean) => {
    setOpen(null);
    if (changed) data.reload();
  };
  const d = data.data;

  return (
    <Page
      title="Bonus"
      actions={
        <>
          <label className="inline-field">
            <Caption1>As of</Caption1>
            <DateInput type="date" size="small" value={asOf} onChange={(_, x) => x.value && setAsOf(x.value)} aria-label="As of" />
          </label>
          {d && <ArchivedSwitch count={d.archivedCount ?? 0} on={showArchived} onChange={setShowArchived} />}
          {editable && d && <Button onClick={() => setOpen({ kind: "types" })}>Bonus types…</Button>}
        </>
      }
    >
      <Caption1>
        Each person's targets come from their compensation letter, per bonus type. An override replaces one type's amount, or the whole
        bonus, from its date, and the cost model uses it. A type's total is the sum of its people's amounts.
      </Caption1>
      <ErrorBar message={data.error} />
      {!d ? (
        data.loading && <Loading />
      ) : (
        <>
          {d.retiredPlans.length > 0 && (
            <MessageBar intent="info" layout="multiline">
              <MessageBarBody>
                <strong>Pool-based plans no longer set anyone's bonus.</strong> Bonus now comes from each person's letter. Kept for
                reference: {d.retiredPlans.map((p) => `${p.name} ${p.year} (${money(p.pool)} pool, ${p.participants} ${p.participants === 1 ? "person" : "people"})`).join("; ")}.
                {editable ? " Enter each person's targets from their letter: click any amount, then New letter." : ""}
              </MessageBarBody>
            </MessageBar>
          )}
          <div className="facts">
            <Fact label="Bonus at target, all letters">
              <span className="big">{money(d.totals.target)}</span>
            </Fact>
            <Fact label="Counting in OTE on this day">
              <span className="big">{money(d.totals.amount)}</span>
            </Fact>
            <Fact label="People with an override">
              <span className="big">{count(d.totals.overrides)}</span>
            </Fact>
            <Fact label="People with pay and no letter">
              <span className="big">{count(d.people.filter((r) => r.pay && !r.bonus.letter).length)}</span>
            </Fact>
          </div>
          <BonusTable data={d} editable={editable} onOpen={setOpen} />
        </>
      )}

      {open?.kind === "letter" && <BonusLetterDialog person={open.row} types={d?.types ?? []} letter={open.row.bonus.letter} onClose={close} />}
      {open?.kind === "override" && <BonusOverrideDialog person={open.row} types={d?.types ?? []} current={open.row.bonus} typeId={open.typeId} onClose={close} />}
      {open?.kind === "end" && <EndRecordDialog person={open.row} recordId={open.recordId} label={open.label} noun="override" onClose={close} />}
      {open?.kind === "void" && <VoidDialog person={open.row} recordId={open.recordId} label={open.label} onClose={close} />}
      {open?.kind === "types" && d && <BonusTypesDialog types={d.types} onClose={close} />}
    </Page>
  );
}

function BonusTable({ data, editable, onOpen }: { data: BonusDashboardResponse; editable: boolean; onOpen: (o: Open) => void }) {
  const { asOf, types, people, totals } = data;
  // Active types, plus a retired one anyone still has an amount for (BP5 flags it).
  const columns = types.filter((t) => t.active || people.some((r) => r.bonus.lines.some((l) => l.typeId === t.id)));
  if (!people.length) return <Body1 className="muted">No one is active on {day(asOf)}.</Body1>;
  return (
    <div className="bonus-scroll">
      <table className="bonus-grid" aria-label="Bonus by person and type">
        <thead>
          <tr>
            <th className="person-col">Person</th>
            <th className="num">Annual pay</th>
            {columns.map((t) => (
              <th key={t.id} className="num">
                {t.name}
                {(!t.active || !t.includedInOte) && <Caption1 className="block muted">{!t.active ? "retired" : "not in OTE"}</Caption1>}
              </th>
            ))}
            <th className="num">Bonus in OTE</th>
            <th className="num">OTE per letter</th>
            <th className="num">OTE with overrides</th>
          </tr>
        </thead>
        <tbody>
          {people.map((r) => (
            <BonusRowView key={r.id} row={r} asOf={asOf} columns={columns} editable={editable} onOpen={onOpen} />
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th className="person-col">Total</th>
            <td />
            {columns.map((t) => {
              const sum = totals.byType.find((x) => x.typeId === t.id);
              return (
                <td key={t.id} className="num total-col">
                  {money(sum?.amount ?? 0)}
                  {sum && sum.amount !== sum.target && <Caption1 className="block muted">target {money(sum.target)}</Caption1>}
                </td>
              );
            })}
            <td className="num total-col">
              {money(totals.amount)}
              {totals.amount !== totals.target && <Caption1 className="block muted">target {money(totals.target)}</Caption1>}
            </td>
            <td />
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function BonusRowView({ row, asOf, columns, editable, onOpen }: { row: BonusRow; asOf: string; columns: BonusType[]; editable: boolean; onOpen: (o: Open) => void }) {
  const inEffect = overridesOn(row.bonusOverrides, asOf);
  const whole = inEffect.find((o) => o.typeId === null);
  const letterItem = (
    <MenuItem key="letter" onClick={() => onOpen({ kind: "letter", row })}>
      {row.bonus.letter ? "New letter…" : "Enter the letter…"}
    </MenuItem>
  );
  const overrideItems = (typeId: string | null, name: string) => {
    const o = inEffect.find((x) => x.typeId === typeId);
    const label = `${name.toLowerCase()} override from ${day(o?.from)}`;
    return [
      <MenuItem key="override" onClick={() => onOpen({ kind: "override", row, typeId })}>
        {o ? `Override ${name.toLowerCase()} again…` : `Override ${name.toLowerCase()}…`}
      </MenuItem>,
      ...(o && !o.to ? [<MenuItem key="end" onClick={() => onOpen({ kind: "end", row, recordId: o.id, label })}>End this override…</MenuItem>] : []),
      ...(o ? [<MenuItem key="void" onClick={() => onOpen({ kind: "void", row, recordId: o.id, label })}>Withdraw this override (entered in error)…</MenuItem>] : []),
    ];
  };

  return (
    <tr>
      <th className="person-col" scope="row">
        <a href={href("people", row.id)}>{row.name}</a>
        {row.warnings.length > 0 && (
          <Tooltip content={row.warnings.join(" ")} relationship="description">
            <Badge appearance="tint" color="warning" size="small" className="warn-badge">
              {row.warnings.length === 1 ? "1 note" : `${row.warnings.length} notes`}
            </Badge>
          </Tooltip>
        )}
        {row.title && <Caption1 className="block muted">{row.title}</Caption1>}
      </th>
      <td className="num">
        {row.pay ? money(row.pay.annual) : <span className="muted">No pay</span>}
        {row.pay && row.pay.type !== "exempt" && <Caption1 className="block muted">{PAY_TYPE_LABEL[row.pay.type as PayType]}</Caption1>}
      </td>
      {columns.map((t) => {
        const line = row.bonus.lines.find((l) => l.typeId === t.id);
        const content = !line ? (
          <span className="muted">—</span>
        ) : (
          <>
            <span className={whole ? "muted" : undefined}>{money(line.amount)}</span>
            {line.override !== null && (
              <Caption1 className="block">
                <Badge appearance="tint" color="warning" size="small">
                  Override
                </Badge>{" "}
                target {money(line.target)}
              </Caption1>
            )}
          </>
        );
        return (
          <td key={t.id} className="num">
            <Editable editable={editable} label={`${row.name}, ${t.name}`} items={[...overrideItems(t.id, t.name), letterItem]}>
              {content}
            </Editable>
          </td>
        );
      })}
      <td className="num total-col">
        <Editable editable={editable} label={`${row.name}, whole bonus`} items={[...overrideItems(null, "the whole bonus"), letterItem]}>
          {money(row.bonus.total)}
          {whole && (
            <Caption1 className="block">
              <Badge appearance="tint" color="warning" size="small">
                Whole-bonus override
              </Badge>
            </Caption1>
          )}
        </Editable>
      </td>
      <td className="num">{row.ote === null ? "—" : money(row.ote)}</td>
      <td className="num">{row.oteNow === null ? "—" : money(row.oteNow)}</td>
    </tr>
  );
}

/** An amount that opens its edits in place (BP4), or plain text for those who can't edit. */
function Editable({ editable, label, items, children }: { editable: boolean; label: string; items: ReactNode[]; children: ReactNode }) {
  if (!editable) return <>{children}</>;
  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className="cell-button" aria-label={`Edit ${label}`}>
          {children}
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>{items}</MenuList>
      </MenuPopover>
    </Menu>
  );
}

/** BP1: rename, add, and retire bonus types. Types are retired, never deleted, because letters point at them. */
function BonusTypesDialog({ types, onClose }: { types: BonusType[]; onClose: (changed: boolean) => void }) {
  const [rows, setRows] = useState<BonusType[]>(() => types.map((t) => ({ ...t })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const set = (i: number, change: Partial<BonusType>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...change } : r)));
  const names = rows.map((r) => r.name.trim().toLowerCase());
  const problems = [
    ...(names.some((n) => !n) ? ["Every bonus type needs a name."] : []),
    ...(new Set(names).size < names.length ? ["Two bonus types share a name."] : []),
  ];

  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/reference/bonus-types", { types: rows.map((r) => ({ ...r, name: r.name.trim(), id: r.id || undefined })) });
      onClose(true);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, x) => !x.open && onClose(false)}>
      <DialogSurface className="dialog-wide">
        <DialogBody>
          <DialogTitle>Bonus types</DialogTitle>
          <DialogContent className="dialog-stack">
            <Caption1>
              Letters state a target for each type. A type that counts in OTE is carried in project cost; one that doesn't is shown but
              not costed. Retire a type instead of removing it, so earlier letters still read true.
            </Caption1>
            <table className="edit-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Counts in OTE</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || `new-${i}`}>
                    <td>
                      <Input value={r.name} onChange={(_, x) => set(i, { name: x.value })} aria-label="Bonus type name" autoFocus={!r.id} />
                    </td>
                    <td>
                      <Switch checked={r.includedInOte} onChange={(_, x) => set(i, { includedInOte: x.checked })} aria-label={`${r.name || "New type"} counts in OTE`} />
                    </td>
                    <td>
                      <Switch checked={r.active} label={r.active ? "Active" : "Retired"} onChange={(_, x) => set(i, { active: x.checked })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button size="small" onClick={() => setRows((rs) => [...rs, { id: "", name: "", active: true, includedInOte: true }])}>
              Add a type
            </Button>
            <Warnings items={problems} />
          </DialogContent>
          <ErrorBar message={error} />
          <DialogActions>
            <Button onClick={() => onClose(false)}>Cancel</Button>
            <Button appearance="primary" disabled={busy || problems.length > 0} onClick={() => void save()}>
              {busy ? "Saving…" : "Save types"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
